/**
 * ⚡ 性能监测和自适应工具 - V2X可视化的性能守护神
 * =====================================================
 *
 * 🎭 【概念比喻】
 * 想象一下专业赛车手的仪表盘系统：
 * • 实时监控引擎转速（帧率FPS）
 * • 自动检测最佳性能档位（目标帧率）
 * • 根据赛道条件智能调整（自适应优化）
 * • 就像F1赛车的遥测系统，精确掌控每一帧的性能
 *
 * 🔬 【技术原理】
 * • 使用requestAnimationFrame精确测量实际渲染频率
 * • 自动检测用户屏幕刷新率并适配最佳帧率目标
 * • 提供实时性能数据供应用层进行智能优化决策
 * • 支持观察者模式实时通知性能变化
 *
 * 🎯 【应用场景】
 * • V2X道路可视化性能实时监控
 * • 移动端设备性能自适应调整
 * • 大规模3D场景的智能性能优化
 * • 开发阶段的性能调试和分析
 *
 * 📊 【核心价值】
 * • 🎯 实时监控：毫秒级FPS统计
 * • 🤖 智能适配：自动检测最佳帧率
 * • 📱 跨平台：支持不同刷新率设备
 * • 🔔 事件驱动：性能变化实时通知
 */

/**
 * ⚙️ 性能配置接口 - 自定义监控规则
 *
 * 🎭 【比喻】就像赛车的调校参数：
 * • targetFPS：期望的引擎转速（目标帧率）
 * • autoDetectRefreshRate：是否自动检测赛道类型
 * • enableAdaptiveFPS：智能档位切换功能
 * • 帧率范围：引擎转速的安全工作区间
 */
export interface PerformanceConfig {
  targetFPS?: number; // 🎯 目标帧率（FPS）
  autoDetectRefreshRate?: boolean; // 🔍 自动检测屏幕刷新率
  enableAdaptiveFPS?: boolean; // 🤖 启用自适应帧率调整
  minFPS?: number; // 📉 最低帧率限制
  maxFPS?: number; // 📈 最高帧率限制
}

/**
 * ⚡ 性能监控器类 - 专业的性能分析师
 *
 * 🎭 【职业比喻】
 * 这个类就像一位经验丰富的性能调优专家：
 * • 实时监控系统运行状态（FPS统计）
 * • 智能分析硬件能力（刷新率检测）
 * • 提供优化建议和自动调整（自适应帧率）
 * • 为开发者提供详细的性能报告
 *
 * 🔧 【核心功能】
 * • 📊 实时FPS计算和统计
 * • 🔍 自动屏幕刷新率检测
 * • 🎯 智能目标帧率调整
 * • 🔔 性能变化事件通知
 * • 📱 设备性能信息收集
 * • ⚙️ 灵活的配置和控制接口
 *
 * @example
 * ```typescript
 * // 创建性能监控器
 * const perfMonitor = new PerformanceMonitor({
 *   targetFPS: 60,              // 目标60FPS
 *   autoDetectRefreshRate: true, // 自动检测刷新率
 *   enableAdaptiveFPS: true,    // 启用自适应
 *   minFPS: 30,                 // 最低30FPS
 *   maxFPS: 120                 // 最高120FPS
 * });
 *
 * // 监听性能变化
 * perfMonitor.onFPSUpdate((fps) => {
 *   console.log(`当前FPS: ${fps}`);
 *   if (fps < 30) {
 *     // 性能不足，降低画质
 *     reduceQuality();
 *   }
 * });
 *
 * // 在渲染循环中更新
 * function animate() {
 *   perfMonitor.updateFPS(performance.now());
 *   renderer.render(scene, camera);
 *   requestAnimationFrame(animate);
 * }
 * ```
 */
export class PerformanceMonitor {
  // 📊 性能数据存储
  private refreshRate: number = 60; // 屏幕刷新率
  private targetFPS: number = 60; // 目标帧率
  private currentFPS: number = 60; // 当前实际帧率
  private frameCount: number = 0; // 帧计数器
  private lastTime: number = 0; // 上次统计时间

  // ⚙️ 配置和回调管理
  private config: PerformanceConfig; // 配置选项
  private callbacks: ((fps: number) => void)[] = []; // FPS更新回调列表

  /**
   * 🏗️ 构造函数 - 初始化性能监控系统
   *
   * 🎭 【比喻】就像给赛车安装专业仪表盘：
   * • 设置默认的监控参数
   * • 校准仪表指针到合理位置
   * • 根据需要启动自动检测功能
   * • 为后续的精确测量做准备
   *
   * 🔧 【初始化流程】
   * 1. 合并用户配置与默认配置
   * 2. 设置初始目标帧率
   * 3. 根据配置决定是否自动检测刷新率
   * 4. 启动屏幕特性检测流程
   *
   * 💡 **默认配置说明**：
   * • targetFPS=60：主流设备的标准帧率
   * • autoDetectRefreshRate=true：自动适配设备能力
   * • enableAdaptiveFPS=true：智能性能调优
   * • 帧率范围：覆盖低端到高端设备
   *
   * @param config - ⚙️ 性能监控配置选项
   */
  constructor(config: PerformanceConfig = {}) {
    // 📋 合并配置（用户配置覆盖默认值）
    this.config = {
      targetFPS: 60, // 🎯 默认60FPS目标
      autoDetectRefreshRate: true, // 🔍 默认自动检测刷新率
      enableAdaptiveFPS: true, // 🤖 默认启用自适应调整
      minFPS: 30, // 📉 最低30FPS（可用底线）
      maxFPS: 120, // 📈 最高120FPS（高刷屏支持）
      ...config, // 🔄 用户自定义配置覆盖
    };

    // 🎯 设置初始目标帧率
    this.targetFPS = this.config.targetFPS!;

    // 🔍 根据配置启动刷新率自动检测
    if (this.config.autoDetectRefreshRate) {
      this.detectRefreshRate();
    }
  }

  /**
   * 🔍 检测屏幕刷新率 - 智能硬件能力探测
   *
   * 🎭 【比喻】就像专业调车师测试引擎极限：
   * • 连续运行一段时间观察最大转速
   * • 统计平均性能表现
   * • 根据硬件能力设定合理目标
   * • 为后续优化提供科学依据
   *
   * 🔬 【检测原理】
   * • 使用requestAnimationFrame精确计时
   * • 连续统计1秒内的实际刷新次数
   * • 计算平均刷新率并圆整到最接近值
   * • 根据检测结果调整目标帧率
   *
   * 📊 **刷新率适配逻辑**：
   * • 60Hz：标准显示器，目标60FPS
   * • 90Hz/120Hz：高刷屏，适当提高目标
   * • 144Hz+：游戏显示器，启用高性能模式
   * • 自适应：根据maxFPS配置限制上限
   *
   * ⚡ **性能影响**：
   * • 检测过程约1秒，对用户体验影响很小
   * • 只在初始化时执行一次
   * • 为整个应用生命周期提供准确基准
   *
   * @returns {Promise<void>} 🔮 检测完成的Promise
   * @private
   */
  private async detectRefreshRate(): Promise<void> {
    return new Promise(resolve => {
      let frameCount = 0; // 🔢 帧计数器
      const startTime = performance.now(); // ⏱️ 开始时间戳

      // 🔄 递归检测函数
      const detectFrame = () => {
        frameCount++; // 📈 增加帧计数
        const currentTime = performance.now(); // ⏰ 当前时间
        const elapsed = currentTime - startTime; // ⏱️ 已用时间

        // 🎯 检测满1秒时计算结果
        if (elapsed >= 1000) {
          // 📊 计算实际刷新率（帧数/秒）
          this.refreshRate = Math.round(frameCount * (1000 / elapsed));
          console.log(`[V2X-Three] 检测到屏幕刷新率: ${this.refreshRate}Hz`);

          // 🤖 根据刷新率调整目标帧率（如果启用自适应）
          if (this.config.enableAdaptiveFPS) {
            // 🎯 目标帧率 = min(刷新率, 最大FPS限制)
            this.targetFPS = Math.min(this.refreshRate, this.config.maxFPS!);

            // 🔒 确保不低于最小FPS限制
            this.targetFPS = Math.max(this.targetFPS, this.config.minFPS!);
          }

          resolve(); // ✅ 检测完成
        } else {
          // 🔄 继续下一帧检测
          requestAnimationFrame(detectFrame);
        }
      };

      // 🚀 启动检测循环
      requestAnimationFrame(detectFrame);
    });
  }

  /**
   * 📱 获取屏幕信息 - 设备能力全面报告
   *
   * 🎭 【比喻】就像查看赛车的技术规格表：
   * • 屏幕分辨率（赛道规格）
   * • 像素密度（细节清晰度）
   * • 刷新率（引擎最大转速）
   * • 目标帧率（推荐工作转速）
   *
   * 🔬 【信息收集】
   * • 窗口尺寸：当前可视区域大小
   * • 像素密度：高分屏适配信息
   * • 刷新率：硬件显示能力
   * • 目标帧率：推荐性能目标
   *
   * 💡 **用途示例**：
   * • 自适应画质设置
   * • 性能监控仪表盘
   * • 调试信息收集
   * • 用户体验优化决策
   *
   * @returns {Object} 📋 设备屏幕信息对象
   */
  getScreenInfo() {
    return {
      width: window.innerWidth, // 🖥️ 窗口宽度
      height: window.innerHeight, // 🖥️ 窗口高度
      pixelRatio: window.devicePixelRatio, // 📱 像素密度比例
      refreshRate: this.refreshRate, // 🔄 屏幕刷新率
      targetFPS: this.targetFPS, // 🎯 推荐目标帧率
    };
  }

  /**
   * 📊 更新帧率统计 - 实时性能数据采集
   *
   * 🎭 【比喻】就像赛车遥测系统的数据记录：
   * • 每一帧都精确记录时间戳
   * • 累积统计计算平均性能
   * • 定期生成性能报告
   * • 实时通知性能变化
   *
   * 🔬 【统计算法】
   * • 帧计数累积：记录渲染帧数
   * • 时间窗口：每秒统计一次
   * • 平均计算：frames * 1000 / elapsed
   * • 四舍五入：提供整数FPS便于理解
   *
   * 📈 **性能趋势**：
   * • 稳定60FPS：性能良好
   * • 波动较大：可能存在性能瓶颈
   * • 持续低于目标：需要优化或降低画质
   * • 高于目标：可以考虑提升画质
   *
   * ⚡ **调用频率**：
   * • 建议在每帧渲染后调用
   * • 传入performance.now()获得精确时间
   * • 自动处理统计周期和回调触发
   *
   * @param currentTime - ⏰ 当前时间戳（performance.now()）
   *
   * @example
   * ```typescript
   * function renderLoop() {
   *   const startTime = performance.now();
   *
   *   // 执行渲染
   *   renderer.render(scene, camera);
   *
   *   // 更新性能统计
   *   perfMonitor.updateFPS(startTime);
   *
   *   requestAnimationFrame(renderLoop);
   * }
   * ```
   */
  updateFPS(currentTime: number) {
    // 📈 增加帧计数
    this.frameCount++;

    // 🕐 检查是否到达统计周期（1秒）
    if (currentTime - this.lastTime >= 1000) {
      // 📊 计算当前FPS = 帧数 * 1000 / 经过时间
      this.currentFPS = Math.round(
        (this.frameCount * 1000) / (currentTime - this.lastTime)
      );

      // 🔄 重置计数器为下个周期做准备
      this.frameCount = 0;
      this.lastTime = currentTime;

      // 🔔 通知所有订阅者性能更新
      this.callbacks.forEach(callback => callback(this.currentFPS));
    }
  }

  /**
   * 🔔 订阅FPS更新 - 注册性能监听器
   *
   * 🎭 【比喻】就像给赛车安装警报系统：
   * • 当转速异常时立即通知车手
   * • 支持多个监听器同时工作
   * • 提供取消订阅避免内存泄漏
   * • 实时响应性能变化
   *
   * 🔬 【设计模式】
   * • 观察者模式：一对多通知机制
   * • 回调注册：动态添加监听逻辑
   * • 取消机制：返回清理函数
   * • 类型安全：TypeScript完整支持
   *
   * 💡 **使用场景**：
   * • 性能监控UI更新
   * • 自动画质调整触发
   * • 性能数据记录和分析
   * • 开发阶段的调试信息
   *
   * @param callback - 🎯 FPS更新时的回调函数
   * @returns {Function} 🗑️ 取消订阅的函数
   *
   * @example
   * ```typescript
   * // 注册性能监听
   * const unsubscribe = perfMonitor.onFPSUpdate((fps) => {
   *   // 更新性能显示
   *   document.getElementById('fps').textContent = `${fps} FPS`;
   *
   *   // 自动画质调整
   *   if (fps < 30) {
   *     reduceRenderQuality();
   *   } else if (fps > 55) {
   *     increaseRenderQuality();
   *   }
   * });
   *
   * // 组件销毁时清理
   * onDestroy(() => {
   *   unsubscribe();
   * });
   * ```
   */
  onFPSUpdate(callback: (fps: number) => void) {
    // 📝 将回调函数添加到监听列表
    this.callbacks.push(callback);

    // 🔄 返回取消订阅函数（闭包保持引用）
    return () => {
      const index = this.callbacks.indexOf(callback);
      if (index > -1) {
        this.callbacks.splice(index, 1); // 🗑️ 从列表中移除
      }
    };
  }

  /**
   * 📊 获取当前FPS - 实时性能查询
   *
   * 🎭 【比喻】就像查看赛车当前转速表：
   * • 即时获取最新的性能数据
   * • 用于性能判断和决策
   * • 支持外部系统主动查询
   * • 提供同步的性能信息
   *
   * @returns {number} 📈 当前帧率数值
   */
  getCurrentFPS(): number {
    return this.currentFPS;
  }

  /**
   * 🎯 获取目标FPS - 性能基准查询
   *
   * 🎭 【比喻】就像查看推荐的巡航转速：
   * • 返回当前设定的性能目标
   * • 用于性能对比和评估
   * • 帮助判断是否达到预期性能
   * • 支持动态性能调整决策
   *
   * @returns {number} 🎯 目标帧率数值
   */
  getTargetFPS(): number {
    return this.targetFPS;
  }

  /**
   * 🎛️ 设置目标FPS - 动态性能调整
   *
   * 🎭 【比喻】就像调整赛车的目标转速：
   * • 根据赛道条件动态调整目标
   * • 确保设定值在安全范围内
   * • 立即生效影响后续性能评估
   * • 支持运行时的性能策略切换
   *
   * 🔬 【安全限制】
   * • 最小值：不低于minFPS配置
   * • 最大值：不超过maxFPS配置
   * • 自动钳制：确保参数在合理范围
   *
   * @param fps - 🎯 新的目标帧率值
   *
   * @example
   * ```typescript
   * // 根据设备性能调整目标
   * if (isLowEndDevice()) {
   *   perfMonitor.setTargetFPS(30); // 低端设备降低目标
   * } else if (isHighEndDevice()) {
   *   perfMonitor.setTargetFPS(90); // 高端设备提升目标
   * }
   * ```
   */
  setTargetFPS(fps: number) {
    // 🔒 应用安全范围限制（钳制到配置范围内）
    this.targetFPS = Math.max(
      this.config.minFPS!, // 📉 不低于最小值
      Math.min(fps, this.config.maxFPS!) // 📈 不超过最大值
    );
  }

  /**
   * 📡 获取刷新率 - 硬件能力查询
   *
   * 🎭 【比喻】就像查看引擎的最大转速限制：
   * • 返回硬件支持的最大刷新频率
   * • 用于了解设备显示能力上限
   * • 帮助制定合理的性能策略
   * • 支持硬件适配和优化决策
   *
   * @returns {number} 🔄 屏幕刷新率（Hz）
   */
  getRefreshRate(): number {
    return this.refreshRate;
  }
}

/**
 * 🎉 PerformanceMonitor 使用总结
 * ========================================
 *
 * 🌟 **核心优势**：
 * • ⚡ 实时监控：精确的FPS统计和性能分析
 * • 🤖 智能适配：自动检测设备能力并调整目标
 * • 🔔 事件驱动：性能变化实时通知机制
 * • 📊 数据丰富：全面的设备和性能信息
 * • 🎯 灵活控制：支持动态调整和自定义配置
 *
 * 🎨 **技术特色**：
 * • 🔍 刷新率自动检测算法
 * • 📈 高精度FPS计算和统计
 * • 🔔 观察者模式的回调机制
 * • ⚙️ 灵活的配置和范围限制
 * • 🛡️ 安全的参数验证和钳制
 *
 * ⚡ **性能优化**：
 * • 轻量级统计算法，对性能影响极小
 * • 智能检测只在初始化时执行一次
 * • 回调机制支持高效的事件分发
 * • 内存友好的数据结构设计
 * • 精确的时间计算减少误差累积
 *
 * 🎯 **应用场景**：
 * • V2X交通可视化性能监控
 * • 移动端3D应用自适应优化
 * • 游戏和实时渲染性能分析
 * • 开发阶段的性能调试工具
 * • 大屏显示系统性能管理
 *
 * 💡 **最佳实践**：
 * • 在渲染循环中定期调用updateFPS()
 * • 使用回调机制实现自动画质调整
 * • 根据设备类型设置合理的FPS范围
 * • 结合其他性能指标进行综合分析
 * • 生产环境可用于性能监控和报警
 */
